import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { RowGroup } from '@/components/Rows'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import {
	type ExpectationStep,
	type ExpectationVideo,
	expectationsFor,
} from '@/features/visits/expectations'
import { analytics } from '@/lib/analytics'
import { Pressable } from '@/ui/core/pressable'
import { Text } from '@/ui/core/typography'
import { Image } from 'expo-image'
import { VideoView, useVideoPlayer } from 'expo-video'
import { useRef } from 'react'
import { StyleSheet, View } from 'react-native'

/**
 * How tall the still is inside the group.
 *
 * A hundred and thirty-two, which is sixteen-by-nine across the group's own width — so the card
 * is the film's shape rather than a band cropped to fit a list.
 */
const STILL_HEIGHT = 132

/** The disc that says the still is a film and not a photograph. */
const PlayMark = () => (
	<View
		className="absolute items-center justify-center rounded-full"
		style={{
			height: 46,
			width: 46,
			top: '50%',
			left: '50%',
			marginTop: -23,
			marginLeft: -23,
			backgroundColor: 'rgba(255,255,255,0.94)',
		}}
	>
		{/* Nudged right by two: a triangle's optical centre is behind its point, so a play mark
		    centred on its bounding box reads as sitting left in the disc. */}
		<View style={{ marginLeft: 2 }}>
			<Glyph name="play" size={15} color={colors.ink} />
		</View>
	</View>
)

/**
 * The film, as a card inside the group — and full screen once it is tapped.
 *
 * The frame is the system's own player: it rotates, scrubs, AirPlays and closes with the gesture
 * everybody already knows. It was a sixteen-by-nine box in a sheet over the page, which is the
 * right frame for a preview and the wrong one for the subject — a forty-second film shown at a
 * third of a screen with the page it came from greyed out behind it.
 *
 * The `VideoView` is mounted under the poster at zero opacity rather than in place of it. It has
 * to be laid out before it can be asked to go full screen, and a player showing its own first
 * frame is a black card until the file loads, where the poster is a photograph of the studio from
 * the moment the page appears.
 *
 * Mounted on white rather than bleeding to the group's edge: the still gets an edge of its own
 * without a second colour, which is the same move the visit card makes with its photograph. Its
 * length is on the card because that is the fact that decides whether anyone taps it.
 */
const VideoCard = ({ video }: { video: ExpectationVideo }) => {
	const view = useRef<VideoView>(null)
	const player = useVideoPlayer(video.source, (instance) => {
		instance.loop = false
	})

	return (
		<Pressable
			onPress={async () => {
				analytics.trackClick('visit-expect-video')
				await view.current?.enterFullscreen()
				player.play()
			}}
			accessibilityRole="button"
			accessibilityLabel={`Play ${video.title}, ${video.length}`}
			className="rounded-[18px] bg-white p-1.5"
		>
			<View
				className="overflow-hidden rounded-[13px] bg-ink"
				style={{ height: STILL_HEIGHT }}
			>
				<VideoView
					ref={view}
					player={player}
					style={StyleSheet.absoluteFill}
					contentFit="contain"
					nativeControls
					allowsFullscreen
				/>
				<Image
					source={video.poster}
					style={StyleSheet.absoluteFill}
					contentFit="cover"
					contentPosition={{ left: '50%', top: '42%' }}
					transition={200}
					accessibilityLabel=""
				/>
				{/* Dark enough at the foot to hold two white lines, and barely there at the top
				    so the still is still a still. */}
				<View
					className="absolute inset-0"
					style={{ backgroundColor: 'rgba(0,0,0,0.20)' }}
				/>
				<PlayMark />
				<View className="absolute right-3 bottom-2.5 left-3 flex-row items-end justify-between">
					<Text size="body1" weight="bold" tone="white">
						{video.title}
					</Text>
					<Text size="body2" weight="bold" tone="white" className="opacity-85">
						{video.length}
					</Text>
				</View>
			</View>
		</Pressable>
	)
}

/**
 * One step, numbered.
 *
 * The number takes the disc a row's mark would have, because the list is an order and a mark that
 * is not a number leaves the reader working out that it is one. White on the blush group, which
 * is the same disc every other row in the app draws.
 */
const Step = ({
	step,
	at,
	first,
}: { step: ExpectationStep; at: number; first: boolean }) => (
	<View className="flex-row py-3" style={{ gap: 12 }}>
		{/* Drawn by the lower of the two rows, inset to the words — the rule the card rows follow. */}
		{!first && (
			<View
				className="absolute top-0 h-px"
				style={{ left: 52, right: 0, backgroundColor: colors.divider }}
			/>
		)}
		<View className="h-10 w-10 items-center justify-center rounded-full bg-white">
			<Text size="body0" weight="bold">
				{at + 1}
			</Text>
		</View>
		<View className="flex-1">
			<Text size="body1" weight="bold">
				{step.title}
			</Text>
			<Text size="body2" tone="soft" className="mt-0.5">
				{step.line}
			</Text>
		</View>
	</View>
)

/**
 * What happens at this visit: the film, then the steps in order.
 *
 * A titled group like every other section in the app — nothing to open, simply there. It was
 * drawn twice for a while, the second time inside a sheet that Home's card opened; Home keeps
 * only the two rows a member wants while walking, so there is one presentation again.
 *
 * Nothing at all where the visit's types have no copy written for them — see `expectationsFor`.
 * A visit page is not worse for lacking this section; it is much worse for guessing.
 */
export const VisitExpectations = ({ visit }: { visit: UpcomingVisit }) => {
	const group = expectationsFor(visit)
	if (!group) {
		return null
	}

	return (
		<RowGroup title="What to expect">
			{!!group.video && (
				<View className="mt-3 mb-0.5">
					<VideoCard video={group.video} />
				</View>
			)}
			{group.steps.map((step, at) => (
				<Step key={step.title} step={step} at={at} first={at === 0} />
			))}
		</RowGroup>
	)
}
